import type { LoggerBody, LoggerPayload } from '@heiland/logger'
import type { SeverityLevel } from '@sentry/vue'

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {}

export type LogBody = Prettify<Partial<Omit<LoggerBody, 'metadata'>> & Pick<LoggerBody, 'title' | 'message'>>

declare module '#app' {
  // @ts-expect-error For some reason that might throw an error, though it's valid according to the docs
  interface NuxtApp {
    $log(level: string, body: LogBody): void
    $storeErrorLog(filePath: string, internalLoggerMessage: string, snackbarMessage: string, error: any): void
  }
}

function sendToSentry($sentry: any, payload: Omit<LoggerPayload, 'slackWebhookUrl'>) {
  let sentryLevel: SeverityLevel = payload.level as SeverityLevel
  switch (payload.level) {
    case 'warn':
      sentryLevel = 'warning'
      break
  }
  $sentry.captureMessage(`${payload.body.message}`, {
    user: { id: payload.body.metadata.customerHandle },
    level: sentryLevel,
    tags: {
      'heiland-trace-id': payload.body.traceId,
      'threadName': payload.body.threadName,
      'device': payload.body.metadata.device,
    },
    extra: payload.body.stack,
  })
}
export default defineNuxtPlugin(() => {
  const userStore = useUserStore()
  const { baseURL } = useRuntimeConfig().app

  return {
    provide: {
      log(level: string, body: LogBody) {
        const { $sentry } = useNuxtApp()

        let threadName = import.meta.server ? 'server' : 'client'
        if (body.threadName)
          threadName += ` - ${body.threadName}`
        const payload: Omit<LoggerPayload, 'slackWebhookUrl'> = {
          level,
          consoleOnly: true, // TODO may we can remove the slack logger since were using sentry. error.statusCode && error.statusCode < 500,,
          body: {
            ...body,
            threadName,
            metadata: {
              customerHandle: userStore.data?.handle || '',
            },
            stack: body.stack ? JSON.parse(JSON.stringify(body.stack, Object.getOwnPropertyNames(body.stack))) : null,
          },
        }
        useFetch('/api/logger', {
          method: 'POST',
          body: payload,
          baseURL,
        })
        if ($sentry)
          sendToSentry($sentry, payload)
      },
      storeErrorLog(filePath: string, internalLoggerMessage: string, snackbarMessage: string, error: any) {
        const { $sentry, $snackbar, $i18n } = useNuxtApp()

        if (error.statusCode === 401)
          $snackbar.showMessage({ text: $i18n.t('snackbar.messages.auth.expired'), color: 'error', closeText: $i18n.t('close') })
        else $snackbar.showMessage({ text: snackbarMessage, color: 'error', closeText: $i18n.t('close') })

        let threadName = import.meta.server ? 'server' : 'client'
        if (filePath)
          threadName += ` - ${filePath}`
        const payload: Omit<LoggerPayload, 'slackWebhookUrl'> = {
          level: 'error',
          consoleOnly: true, // TODO may we can remove the slack logger since were using sentry. error.statusCode && error.statusCode < 500,
          body: {
            title: 'Error in pinia store',
            message: internalLoggerMessage,
            threadName,
            traceId: error.data && error.data.data ? error.data.data.traceId : undefined,
            metadata: {
              customerHandle: userStore.data?.handle || '',
            },
            stack: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
          },
        }

        useFetch('/api/logger', {
          method: 'POST',
          body: payload,
          baseURL,
        })
        // We do not want to send 404s and 422s and 401s and 400s to sentry
        if (error.statusCode === 404 || error.statusCode === 422 || error.statusCode === 401 || error.statusCode === 400)
          return
        if ($sentry)
          sendToSentry($sentry, payload)
      },
    },
  }
})
